/**
 * USE WALLET PROFILE HOOK
 * 
 * Fetches user profile information associated with a wallet address.
 * Used for displaying verified profiles throughout the app.
 * 
 * Returns:
 * - profile: { userId, displayName, avatarUrl, bio, verified, verifiedAt } or null
 * - loading: boolean
 * - error: string or null
 * - refresh: function to reload data
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useWalletProfile(walletAddress) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = async () => {
    if (!walletAddress) {
      setProfile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, check if wallet is linked to a user
      const { data: walletData, error: walletError } = await supabase
        .from('user_wallets')
        .select('user_id, verified, verified_at')
        .eq('address', walletAddress)
        .maybeSingle();

      if (walletError) throw walletError;

      if (!walletData) {
        // Wallet not linked to any profile
        setProfile(null);
        setLoading(false);
        return;
      }

      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profile')
        .select('id, display_name, avatar_url, bio, created_at')
        .eq('id', walletData.user_id)
        .maybeSingle();

      if (profileError) throw profileError;
      
      if (!profileData) {
        // User profile not found
        console.warn('User profile not found for user_id:', walletData.user_id);
        setProfile(null);
        setLoading(false);
        return;
      }

      // Combine data
      setProfile({
        userId: profileData.id,
        displayName: profileData.display_name,
        avatarUrl: profileData.avatar_url,
        bio: profileData.bio,
        verified: walletData.verified,
        verifiedAt: walletData.verified_at,
        createdAt: profileData.created_at
      });
    } catch (err) {
      console.error('Error loading wallet profile:', err);
      setError(err.message);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [walletAddress]);

  return {
    profile,
    loading,
    error,
    refresh: loadProfile
  };
}

/**
 * USE USER WALLETS HOOK
 * 
 * Fetches all wallets linked to a user profile.
 * 
 * Returns:
 * - wallets: array of { address, verified, verifiedAt, createdAt }
 * - loading: boolean
 * - error: string or null
 * - refresh: function to reload data
 */
export function useUserWallets(userId) {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadWallets = async () => {
    if (!userId) {
      setWallets([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('user_wallets')
        .select('address, verified, verified_at, created_at')
        .eq('user_id', userId)
        .order('verified', { ascending: false })
        .order('verified_at', { ascending: false, nullsFirst: false });

      if (fetchError) throw fetchError;

      setWallets(data || []);
    } catch (err) {
      console.error('Error loading user wallets:', err);
      setError(err.message);
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallets();
  }, [userId]);

  return {
    wallets,
    loading,
    error,
    refresh: loadWallets
  };
}

/**
 * USE CREATE PROFILE HOOK
 * 
 * Creates a new user profile.
 * 
 * Returns:
 * - createProfile: async function(displayName, avatarUrl, bio) => userId
 * - creating: boolean
 * - error: string or null
 */
export function useCreateProfile() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const createProfile = async (displayName = '', avatarUrl = '', bio = '') => {
    setCreating(true);
    setError(null);

    try {
      const { data, error: insertError } = await supabase
        .from('user_profile')
        .insert({
          display_name: displayName,
          avatar_url: avatarUrl,
          bio: bio
        })
        .select('id')
        .maybeSingle();

      if (insertError) throw insertError;
      
      if (!data) {
        throw new Error('Failed to create profile - no data returned');
      }

      return data.id;
    } catch (err) {
      console.error('Error creating profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setCreating(false);
    }
  };

  return {
    createProfile,
    creating,
    error
  };
}

